import styled, { css } from 'styled-components';
import { getCDNImageUrl } from '@/helpers/cdn';
import { ThemeMode } from '@/helpers/enums';
import type { RegistrationConfig } from '@/modules/registration/helpers/types';
import { hexToRGB, typography } from '@/styles/helpers';

const errorIcon = getCDNImageUrl('/icon-img/tickCircleDeclineIcon.svg');

export interface ScFormGroupProps {
  marginTop?: number;
  marginBottom?: number;
  minWidth?: string;
  marginBottomOff?: boolean;
  disabled?: boolean;
  mode?: ThemeMode;
  wide?: boolean;
  styleScheme?: RegistrationConfig['styleScheme'];
}

export const ScFormGroup = styled.div<ScFormGroupProps>`
  position: relative;
  margin-bottom: ${({ marginBottomOff }) => (marginBottomOff ? 0 : '16px')};

  ${({ marginBottom }) =>
    marginBottom &&
    css`
      margin-bottom: ${marginBottom}px;
    `}

  margin-top: ${({ marginTop }) => (marginTop ? `${marginTop}px` : 0)};
  display: flex;
  flex-direction: column;
  gap: 4px;

  ${({ minWidth }) =>
    minWidth &&
    css`
      min-width: ${minWidth};
    `};

  ${({ wide }) =>
    wide &&
    css`
      width: 100%;
    `};

  & > h3 {
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0.16px;
    padding: 0 16px;
    color: ${({ theme, mode }) =>
      mode === ThemeMode.Dark ? theme.whiteColor : theme.blackColor};

    ${({ disabled, mode }) =>
      disabled &&
      css`
        color: ${({ theme }) =>
          mode === ThemeMode.Dark
            ? hexToRGB(theme.whiteColor, 0.2)
            : hexToRGB(theme.blackColor, 0.2)};
      `};

    ${({ styleScheme }) =>
      styleScheme?.inputLabel &&
      css`
        color: ${styleScheme.inputLabel.color};
      `};
  }

  & > div > .child {
    &:not(:last-child) {
      margin-bottom: 8px;
    }
  }

  & > .addons {
    text-align: right;
    font-size: 12px;
    line-height: 20px;
  }

  & > .tooltip {
    text-align: left;
    font-size: 12px;
    line-height: 20px;
    color: ${({ theme }) => theme.grayMegaLightColor};
  }

  & .error {
    color: ${({ theme }) => theme.redColor};
    font-size: 12px;
    line-height: 18px;
    padding: 0 16px;
    position: relative;
  }
`;

export const ScFormRegistration = styled.div<{
  styleScheme?: RegistrationConfig['styleScheme'];
}>`
  width: 100%;
  .checkbox-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .label span {
    font-weight: 700;
    text-decoration: underline !important;

    @media (hover: hover) {
      &:hover {
        color: ${({ theme }) => theme.secondaryColorHover};

        ${({ styleScheme }) =>
          styleScheme?.colorLinks &&
          css`
            color: ${hexToRGB(styleScheme.colorLinks, 0.8)};
          `}
      }
    }
  }

  & > *:last-child {
    margin-bottom: ${({ styleScheme }) =>
      styleScheme?.formMarginBottom || '24px'};
  }
`;

export const ScLink = styled.span<{
  styleScheme?: RegistrationConfig['styleScheme'];
}>`
  color: ${({ theme, styleScheme }) =>
    styleScheme?.colorLinks || theme.secondaryColor};
  cursor: pointer;
`;

export const ScErrorGroup = styled.div`
  text-align: left;
  color: ${({ theme }) => theme.blackColor};
  background: rgba(222, 46, 7, 0.2);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  margin: 32px 0;
  ${typography['body2regular']};

  .icon {
    height: 24px;
    width: 24px;
    object-fit: contain;
    margin-right: 16px;
    flex-shrink: 0;
    background: ${() => `url(${errorIcon}) center no-repeat`};
  }

  ul {
    list-style: none;

    li {
      span {
        text-transform: capitalize;
      }
    }
  }
`;
